import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  CalendarDays, Flame, Trophy, DollarSign,
  TrendingUp, Clock, Star, CheckCircle
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-popover border border-border rounded-xl px-3 py-2 shadow-xl text-sm">
        <p className="font-medium">{label}</p>
        <p className="text-primary font-bold">{payload[0].value} hábitos</p>
      </div>
    );
  }
  return null;
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [weeklyData, setWeeklyData] = useState([]);
  const [habits, setHabits] = useState([]);
  const [habitLogs, setHabitLogs] = useState([]);
  const [quote, setQuote] = useState('');
  const [loading, setLoading] = useState(true);

  const today = new Date().toISOString().split('T')[0];
  const dayNames = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
  const dayOfWeek = dayNames[new Date().getDay()];
  const dateFormatted = new Date().toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, weeklyRes, habitsRes, logsRes, quoteRes] = await Promise.all([
        api.get('/stats'),
        api.get('/stats/weekly'),
        api.get('/habits'),
        api.get(`/habits/logs?date=${today}`),
        api.get('/quotes/random')
      ]);
      setStats(statsRes.data);
      setWeeklyData(weeklyRes.data);
      setHabits(habitsRes.data.filter(h => h.active));
      setHabitLogs(logsRes.data);
      setQuote(quoteRes.data.quote);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [today]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleHabit = async (habitId) => {
    try {
      const res = await api.post(`/habits/${habitId}/toggle?date=${today}`);
      setHabitLogs(prev => {
        const existing = prev.find(l => l.habit_id === habitId);
        if (existing) {
          return prev.map(l => l.habit_id === habitId ? { ...l, completed: res.data.completed } : l);
        }
        return [...prev, { habit_id: habitId, date: today, completed: res.data.completed }];
      });
    } catch (err) {
      console.error(err);
    }
  };

  const isHabitDone = (habitId) => {
    return habitLogs.find(l => l.habit_id === habitId)?.completed || false;
  };

  const habitsCompletedToday = habits.filter(h => isHabitDone(h.id)).length;
  const habitsTotal = habits.length;
  const progressPercent = habitsTotal > 0 ? Math.round((habitsCompletedToday / habitsTotal) * 100) : 0;

  if (loading) {
    return (
      <div className="space-y-6 stagger-children" data-testid="dashboard-loading">
        {[1,2,3].map(i => (
          <div key={i} className="h-32 bg-muted rounded-2xl animate-pulse" />
        ))}
      </div>
    );
  }

  const statCards = [
    { label: 'Dias mantendo o plano', value: stats?.total_clean_days || 0, icon: CalendarDays, color: 'text-primary' },
    { label: 'Streak atual', value: stats?.current_streak || 0, icon: Flame, color: 'text-orange-500' },
    { label: 'Melhor streak', value: stats?.best_streak || 0, icon: Trophy, color: 'text-yellow-500' },
    { label: 'Economizado', value: `R$ ${(stats?.total_savings || 0).toFixed(0)}`, icon: DollarSign, color: 'text-emerald-500' },
    { label: 'Hábitos/semana', value: stats?.habits_this_week || 0, icon: CheckCircle, color: 'text-primary' },
    { label: 'Horas recuperadas', value: `${(stats?.hours_recovered || 0).toFixed(0)}h`, icon: Clock, color: 'text-blue-500' },
  ];

  const athleteSportLabel = () => {
    if (!user?.sport) return '';
    if (user.sport === 'musculacao') return 'musculação';
    return user.sport;
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Greeting */}
      <div className="space-y-1">
        <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">
          Bem-vindo de volta, <span className="text-primary">{user?.name?.split(' ')[0] || 'Usuário'}!</span>
        </h1>
        <p className="text-muted-foreground font-body">{dayOfWeek}, {dateFormatted}</p>
        {user?.is_athlete && athleteSportLabel() && (
          <div className="mt-2">
            <Badge variant="secondary" className="text-xs">
              Atleta de {athleteSportLabel()}
            </Badge>
          </div>
        )}
      </div>

      {/* Motivational Quote */}
      {quote && (
        <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20" data-testid="daily-quote">
          <CardContent className="py-4 px-6">
            <p className="text-sm text-primary italic font-medium flex items-center gap-2">
              <Star className="w-4 h-4 flex-shrink-0" />
              &ldquo;{quote}&rdquo;
            </p>
          </CardContent>
        </Card>
      )}

      {/* Streak Hero */}
      {stats?.current_streak > 0 && (
        <Card className="bg-gradient-to-br from-primary/10 to-emerald-50 dark:from-primary/20 dark:to-emerald-950 border-primary/20" data-testid="streak-hero">
          <CardContent className="py-8 text-center">
            <Flame className="w-10 h-10 text-orange-500 mx-auto mb-2" />
            <p className="text-5xl font-heading font-bold text-primary">{stats.current_streak}</p>
            <p className="text-lg text-muted-foreground mt-1">dias em sequência</p>
            <div className="mt-4">
              <Badge variant="secondary" className="text-sm px-4 py-1">
                <TrendingUp className="w-3 h-3 mr-1" />
                Nível: {stats?.level || 'Iniciante'}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* XP Progress */}
      {stats && (
        <Card data-testid="xp-progress-card">
          <CardContent className="py-4 px-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">{stats.xp} XP</span>
              <span className="text-xs text-muted-foreground">Próximo nível: {stats.next_xp} XP</span>
            </div>
            <Progress value={stats.next_xp > 0 ? Math.min((stats.xp / stats.next_xp) * 100, 100) : 100} className="h-2" />
          </CardContent>
        </Card>
      )}

      {/* Stat Cards Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 stagger-children" data-testid="stats-cards-grid">
        {statCards.map((s, i) => {
          const Icon = s.icon;
          return (
            <Card key={i} className="hover:shadow-md transition-shadow">
              <CardContent className="py-4 px-4">
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={`w-4 h-4 ${s.color}`} />
                  <span className="text-xs text-muted-foreground">{s.label}</span>
                </div>
                <p className="text-2xl font-heading font-bold">{s.value}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Today's Habits */}
      <Card data-testid="today-habits-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-heading">Hábitos de hoje</CardTitle>
            <span className="text-sm text-muted-foreground">{habitsCompletedToday}/{habitsTotal}</span>
          </div>
          <Progress value={progressPercent} className="h-1.5 mt-2" />
        </CardHeader>
        <CardContent className="space-y-2">
          {habits.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Que tal adicionar seu primeiro hábito positivo?</p>
          ) : (
            habits.slice(0, 5).map((habit) => {
              const done = isHabitDone(habit.id);
              return (
                <div
                  key={habit.id}
                  className="flex items-center gap-3 py-2 px-3 rounded-xl hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => toggleHabit(habit.id)}
                  data-testid={`habit-toggle-${habit.id}`}
                >
                  <Checkbox checked={done} className="pointer-events-none" />
                  <span className={`text-sm font-medium flex-1 ${done ? 'line-through text-muted-foreground' : ''}`}>
                    {habit.name}
                  </span>
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: habit.color }} />
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Weekly Chart */}
      <Card data-testid="weekly-chart-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-heading">Progresso semanal</CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ width: '100%', height: 200 }}>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={weeklyData} barSize={24}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="day" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
